import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { loadAdminStore, loadStore, deleteStore } from "./localStorage";
import { buildSignInUrl } from "./helpers/authReturn";

// eslint-disable-next-line no-console
console.log(process.env.NEXT_PUBLIC_API_BASE_URL);
export const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL as string;

export const axiosWithoutToken = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Content-type": "application/json",
  },
});

export const getStore = () => {
  const userStore: any = loadStore();
  const adminStore: any = loadAdminStore();

  // Find whichever store contains a truthy token parameter
  const activeAuthData = adminStore?.token ? adminStore : userStore;
  return activeAuthData;
};

/**
 * The httpOnly session (access_token / refresh_token cookies) is attached by
 * the browser automatically because withCredentials is enabled, so user
 * requests need no manual Authorization header. Admin auth (separate system,
 * Bearer token in sessionStorage) still relies on the header, so it is kept
 * as a fallback when a store token exists.
 */
export const axiosWithToken = (config?: AxiosRequestConfig): AxiosInstance => {
  const activeAuthData = getStore();

  const instance = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...(activeAuthData?.token
        ? { Authorization: `Bearer ${activeAuthData.token}` }
        : {}),
    },
    ...config,
  });

  attachAuthInterceptors(instance);
  return instance;
};

/**
 * Module-level shared refresh promise. Exactly one /auth/refresh_token call is
 * ever in flight app-wide; concurrent 401s all await the SAME promise.
 */
let refreshPromise: Promise<boolean> | null = null;

/** Registered by the AuthProvider so a terminal refresh failure (expired
 * session) routes through the same teardown used during boot. */
let onSessionExpiredHandler: (() => void) | null = null;

export function registerSessionExpiredHandler(handler: () => void) {
  onSessionExpiredHandler = handler;
}

export function runSessionExpiredHandler() {
  if (onSessionExpiredHandler) onSessionExpiredHandler();
}

const REFRESH_URL = "/auth/refresh_token";

async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = axiosWithoutToken
      .get(REFRESH_URL)
      .then((r) => r.status === 200)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function attachAuthInterceptors(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const { response, config } = error;
      const status = response?.status;

      if (status !== 401 || !config) {
        return Promise.reject(error);
      }

      // Never retry the refresh call itself — rotation invalidates it.
      const isRefreshCall =
        typeof config.url === "string" &&
        config.url.includes("/auth/refresh_token");
      if (isRefreshCall) {
        return Promise.reject(error);
      }

      // Never retry a request more than once after refresh.
      const retried = (config as AxiosRequestConfig & { _retried?: boolean })
        ._retried;
      if (retried) {
        return Promise.reject(error);
      }
      (config as AxiosRequestConfig & { _retried?: boolean })._retried = true;

      const refreshed = await refreshSession();
      if (refreshed) {
        return instance(config);
      }

      // Refresh failed — session is expired. Route through the AuthProvider
      // teardown (logout, state reset, notify, redirect).
      runSessionExpiredHandler();
      return Promise.reject(error);
    },
  );
}

export const handleAxiosError = (err: any, thunkAPI: any) => {
  const { message, status } = err.toJSON();

  if (status === 403) {
    deleteStore();
    // Never bounce visitors off public pages — they simply become anonymous.
    const pathname =
      typeof window !== "undefined" ? window.location.pathname : "";
    const isPublicRoute =
      !pathname.startsWith("/user") && !pathname.startsWith("/admin");
    if (!isPublicRoute) {
      window.location.assign(buildSignInUrl(pathname + window.location.search));
    }
  }

  if (message === "Network Error") {
    throw thunkAPI.rejectWithValue({
      status: 500,
      message: "You are offline",
    });
  } else {
    throw thunkAPI.rejectWithValue({
      status: err.response.status || 0,
      message: err.response.data.message,
    });
  }
};
